'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User as FirebaseUser,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase-production.config';
import { updateUserPassword } from '@/lib/firebase/auth';
import { UserProfile } from '@/types/user';

// Optimistic profile cache: lets the app render immediately on reload using the
// last known profile while onAuthStateChanged revalidates (and re-enforces the
// active-account check) in the background. The server's Firestore rules remain
// the real authorization gate; this only affects client render timing.
const PROFILE_CACHE_KEY = 'obrapp:userProfile';

function readCachedProfile(): UserProfile | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(PROFILE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      ...parsed,
      lastLoginAt: parsed.lastLoginAt ? new Date(parsed.lastLoginAt) : undefined,
      createdAt: parsed.createdAt ? new Date(parsed.createdAt) : new Date(),
      updatedAt: parsed.updatedAt ? new Date(parsed.updatedAt) : new Date(),
    } as UserProfile;
  } catch {
    return null;
  }
}

function writeCachedProfile(profile: UserProfile | null) {
  if (typeof window === 'undefined') return;
  try {
    if (profile) {
      window.localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(profile));
    } else {
      window.localStorage.removeItem(PROFILE_CACHE_KEY);
    }
  } catch {
    // localStorage unavailable (private mode / quota) — degrade to no cache.
  }
}

interface AuthContextType {
  user: FirebaseUser | null;
  userProfile: UserProfile | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  // NB: there is deliberately no resetPassword here. Password recovery goes
  // through the `requestPasswordReset` server action (src/app/(auth)/forgot-password/actions.ts)
  // so the mail is sent by SendGrid from our authenticated domain, rate-limited
  // and logged — the client SDK's sendPasswordResetEmail used Google's mailer,
  // which silently failed to deliver.
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  updateUserProfile: (data: Partial<UserProfile>) => Promise<void>;
  refreshUserProfile: () => Promise<void>;
  clearError: () => void;
}

// Deliberately defaulted to undefined so the guard in useAuth can actually
// fire. With an object default, a component rendered outside the provider
// silently received `loading: true` forever instead of a clear error.
// Exported so tests can supply a value directly via AuthContext.Provider.
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch user profile from Firestore
  const fetchUserProfile = async (uid: string): Promise<UserProfile | null> => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        return {
          id: uid,
          email: data.email,
          displayName: data.displayName,
          photoURL: data.photoURL,
          role: data.role || 'client',
          companyId: data.companyId,
          bio: data.bio,
          phone: data.phone,
          active: data.active ?? true,
          emailVerified: data.emailVerified ?? false,
          lastLoginAt: data.lastLoginAt?.toDate() || undefined,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  };

  // Sign in with email and password
  const signIn = async (email: string, password: string) => {
    try {
      setError(null);
      const credential = await signInWithEmailAndPassword(auth, email, password);

      // Fetch user profile from Firestore
      const profile = await fetchUserProfile(credential.user.uid);

      if (!profile) {
        await signOut(auth);
        throw new Error('User profile not found. Please contact support.');
      }

      if (!profile.active) {
        await signOut(auth);
        throw new Error('Your account has been deactivated. Please contact support.');
      }

      // Update last login time
      await updateDoc(doc(db, 'users', credential.user.uid), {
        lastLoginAt: serverTimestamp(),
        emailVerified: credential.user.emailVerified,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Sign in failed';
      console.error('Sign in error:', error);
      setError(errorMessage);
      throw error;
    }
  };

  // Clear error state
  const clearError = () => {
    setError(null);
  };

  // Sign out
  const logout = async () => {
    try {
      setError(null);
      await signOut(auth);
      setUser(null);
      setUserProfile(null);
      writeCachedProfile(null);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Logout failed';
      console.error('Logout error:', error);
      setError(errorMessage);
      throw error;
    }
  };

  // Change password (re-auth with current password, then set the new one)
  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      setError(null);
      await updateUserPassword(currentPassword, newPassword);
    } catch (error) {
      const code = (error as { code?: string } | null)?.code;
      const message =
        code === 'auth/wrong-password' || code === 'auth/invalid-credential'
          ? 'La contraseña actual es incorrecta'
          : code === 'auth/requires-recent-login'
            ? 'Por seguridad, vuelve a iniciar sesión antes de cambiar la contraseña'
            : 'No se pudo cambiar la contraseña';
      console.error('Change password error:', error);
      setError(message);
      throw new Error(message);
    }
  };

  // Update user profile
  const updateUserProfile = async (data: Partial<UserProfile>) => {
    if (!user) throw new Error('No authenticated user');

    try {
      setError(null);

      // Update Firebase Auth profile if display name or photo changed
      if (data.displayName !== undefined || data.photoURL !== undefined) {
        await updateProfile(user, {
          displayName: data.displayName ?? user.displayName,
          photoURL: data.photoURL ?? user.photoURL,
        });
      }

      // Update Firestore profile
      await setDoc(
        doc(db, 'users', user.uid),
        {
          ...data,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      // Refresh user profile
      await refreshUserProfile();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Update profile failed';
      console.error('Update profile error:', error);
      setError(errorMessage);
      throw error;
    }
  };

  // Refresh user profile
  const refreshUserProfile = async () => {
    if (!user) return;

    try {
      const profile = await fetchUserProfile(user.uid);
      setUserProfile(profile);
      writeCachedProfile(profile);
    } catch (error) {
      console.error('Error refreshing user profile:', error);
    }
  };

  // Optimistic hydration: render immediately with the last known profile while
  // onAuthStateChanged revalidates below. Runs post-hydration (in an effect, not
  // a useState initializer) so server and client first render stay identical.
  useEffect(() => {
    const cached = readCachedProfile();
    if (cached) {
      setUserProfile(cached);
      setLoading(false);
    }
  }, []);

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      if (firebaseUser) {
        const profile = await fetchUserProfile(firebaseUser.uid);
        setUserProfile(profile);
        writeCachedProfile(profile);

        // Check if account is active
        if (profile && !profile.active) {
          await signOut(auth);
          setUser(null);
          setUserProfile(null);
          writeCachedProfile(null);
          setError('Your account has been deactivated. Please contact support.');
        }
      } else {
        setUserProfile(null);
        writeCachedProfile(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const value = {
    user,
    userProfile,
    loading,
    error,
    signIn,
    logout,
    changePassword,
    updateUserProfile,
    refreshUserProfile,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
